/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2023.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Icon, Tooltip} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QFieldType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldType";
import {QInstance} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QInstance";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {FieldFunction} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/FieldFunction";
import {QCriteriaOperator} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QCriteriaOperator";
import {QFilterCriteria} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QFilterCriteria";
import QContext from "QContext";
import colors from "qqq/assets/theme/base/colors";
import {QFilterCriteriaWithId} from "qqq/components/query/CustomFilterPanel";
import {clampValuesToOperatorOption, getDefaultCriteriaValue, getOperatorOptions, OperatorOption, validateCriteria, ValueMode} from "qqq/components/query/FilterCriteriaRow";
import FilterCriteriaRowValues from "qqq/components/query/FilterCriteriaRowValues";
import XIcon from "qqq/components/query/XIcon";
import {QueryScreenUsage} from "qqq/pages/records/query/RecordQuery";
import FilterUtils from "qqq/utils/qqq/FilterUtils";
import TableUtils from "qqq/utils/qqq/TableUtils";
import React, {SyntheticEvent, useContext, useEffect, useReducer, useState} from "react";

export type CriteriaParamType = QFilterCriteriaWithId | null | "tooComplex";

interface QuickFilterProps
{
   metaData: QInstance;
   tableMetaData: QTableMetaData;
   fullFieldName: string;
   fieldMetaData: QFieldMetaData;
   criteriaParam: CriteriaParamType;
   updateCriteria: (newCriteria: QFilterCriteria, needDebounce: boolean, doRemoveCriteria: boolean) => void;
   defaultOperator?: QCriteriaOperator;
   handleRemoveQuickFilterField?: (fieldName: string) => void;
   queryScreenUsage?: QueryScreenUsage;
   allowVariables?: boolean;

   //////////////////////////////////////////////////////////////////////////////
   // a locked quick filter edits a criterion required by whatever opened this //
   // screen - it may not be cleared or turned off.                            //
   //////////////////////////////////////////////////////////////////////////////
   isLocked?: boolean;
}

QuickFilter.defaultProps =
   {
      defaultOperator: QCriteriaOperator.EQUALS,
      handleRemoveQuickFilterField: null,
      isLocked: false
   };

let seedId = new Date().getTime() % 173237;

export const quickFilterButtonStyles = {
   fontSize: "0.75rem",
   fontWeight: 600,
   color: "#757575",
   borderColor: colors.grayLines.main + " !important",
   textTransform: "none",
   borderRadius: "2rem",
   border: "1px solid #757575",
   minWidth: "3.5rem",
   minHeight: "auto",
   padding: "0.375rem 0.625rem",
   whiteSpace: "nowrap",
   boxShadow: "none",
   marginBottom: "0.5rem",
   marginRight: "0.5rem",
   "&:hover": {
      opacity: "unset",
      background: "unset",
      boxShadow: "unset",
      filter: "unset"
   }
};

/*******************************************************************************
 ** Test if a CriteriaParamType represents an actual query criteria - or, if it's
 ** null or the "tooComplex" placeholder.
 *******************************************************************************/
const criteriaParamIsCriteria = (param: CriteriaParamType): boolean =>
{
   return (param != null && param != "tooComplex");
};

/*******************************************************************************
 ** Test of an OperatorOption equals a query Criteria - that is - that the
 ** operators within them are equal - AND - if the OperatorOption has implicit
 ** values (e.g., the booleans), then those options equal the criteria's options.
 *******************************************************************************/
const doesOperatorOptionEqualCriteria = (operatorOption: OperatorOption, criteria: QFilterCriteriaWithId): boolean =>
{
   if (operatorOption.value == criteria.operator)
   {
      if (operatorOption.implicitValues)
      {
         if (JSON.stringify(operatorOption.implicitValues) == JSON.stringify(criteria.values))
         {
            return (true);
         }
         else
         {
            return (false);
         }
      }

      return (true);
   }

   return (false);
};


/*******************************************************************************
 ** Get the object to use as the selected OperatorOption (e.g., value for that
 ** autocomplete), given an array of options, the query's active criteria in this
 ** field, and the default operator to use for this field
 *******************************************************************************/
const getOperatorSelectedValue = (operatorOptions: OperatorOption[], criteria: QFilterCriteriaWithId, defaultOperator: QCriteriaOperator, return0thOptionInsteadOfNull: boolean = false): OperatorOption =>
{
   if (criteria)
   {
      const filteredOptions = operatorOptions.filter(o => doesOperatorOptionEqualCriteria(o, criteria));
      if (filteredOptions.length > 0)
      {
         return (filteredOptions[0]);
      }
   }

   const filteredOptions = operatorOptions.filter(o => o.value == defaultOperator);
   if (filteredOptions.length > 0)
   {
      return (filteredOptions[0]);
   }

   if (return0thOptionInsteadOfNull)
   {
      console.log("Returning 0th operator instead of null - this isn't expected, but has been seen to happen - so here's some additional debugging:");
      try
      {
         console.log("Operator options:   " + JSON.stringify(operatorOptions));
         console.log("Criteria: " + JSON.stringify(criteria));
         console.log("Default Operator:   " + JSON.stringify(defaultOperator));
      }
      catch (e)
      {
         console.log(`Error in debug output: ${e}`);
      }

      return operatorOptions[0];
   }

   return (null);
};

/*******************************************************************************
 ** Component to render a QuickFilter - that is - a button, with a Menu under it,
 ** with Operator and Value controls.
 *******************************************************************************/
export default function QuickFilter({metaData, tableMetaData, fullFieldName, fieldMetaData, criteriaParam, updateCriteria, defaultOperator, handleRemoveQuickFilterField, queryScreenUsage, allowVariables, isLocked}: QuickFilterProps): JSX.Element
{
   const operatorOptions = fieldMetaData ? getOperatorOptions(metaData, tableMetaData, fullFieldName) : [];
   const [_, tableForField] = TableUtils.getFieldAndTable(tableMetaData, fullFieldName);

   const [isOpen, setIsOpen] = useState(false);
   const [anchorEl, setAnchorEl] = useState(null);
   const [isMouseOver, setIsMouseOver] = useState(false);

   ////////////////////////////////////////////////////////////////////////////////////////////////////////
   // copy the criteriaParam to a new object in here - so changes won't apply until user closes the menu //
   ////////////////////////////////////////////////////////////////////////////////////////////////////////
   const [criteria, setCriteria] = useState(criteriaParamIsCriteria(criteriaParam) ? Object.assign({}, criteriaParam) as QFilterCriteriaWithId : null);
   const [id, setId] = useState(criteriaParamIsCriteria(criteriaParam) ? (criteriaParam as QFilterCriteriaWithId).id : ++seedId);

   const [operatorSelectedValue, setOperatorSelectedValue] = useState(getOperatorSelectedValue(operatorOptions, criteria, defaultOperator, true));
   const [operatorInputValue, setOperatorInputValue] = useState(operatorSelectedValue?.label);

   const {criteriaIsValid, criteriaStatusTooltip} = validateCriteria(criteria, operatorSelectedValue);

   const {accentColor} = useContext(QContext);

   //////////////////////
   // ole' faithful... //
   //////////////////////
   const [, forceUpdate] = useReducer((x) => x + 1, 0);

   useEffect(() =>
   {
      //////////////////////////////////////////////////////////////////////////////
      // was not seeing criteria changes take place until watching it stringified //
      //////////////////////////////////////////////////////////////////////////////
      setCriteria(criteria);
   }, [JSON.stringify(criteria)]);

   /*******************************************************************************
    **
    *******************************************************************************/
   function handleMouseOverElement()
   {
      setIsMouseOver(true);
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function handleMouseOutElement()
   {
      setIsMouseOver(false);
   }


   //////////////////////////////////////////////////////////////////////////////////////////////////////////////
   // handle a change to the criteria from outside this component (e.g., the prop isn't the same as the state) //
   //////////////////////////////////////////////////////////////////////////////////////////////////////////////
   if (criteriaParamIsCriteria(criteriaParam) && JSON.stringify(criteriaParam) !== JSON.stringify(criteria))
   {
      if (isOpen)
      {
         ////////////////////////////////////////////////////////////////////////////////
         // this was firing too-often for case where:  there was a criteria originally //
         ////////////////////////////////////////////////////////////////////////////////
         console.log("Not handling outside change (A), because dropdown is-open");
      }
      else
      {
         ////////////////////////////////////////////////////////////////////////////////////////////////////////
         // copy the criteriaParam to a new object in here - so changes won't apply until user closes the menu //
         ////////////////////////////////////////////////////////////////////////////////////////////////////////
         const newCriteria = Object.assign({}, criteriaParam) as QFilterCriteriaWithId;
         setCriteria(newCriteria);
         const operatorOption = operatorOptions.filter(o => o.value == newCriteria.operator)[0];
         setOperatorSelectedValue(operatorOption);
         setOperatorInputValue(operatorOption.label);
      }
   }

   /*******************************************************************************
    ** Test if we need to construct a new criteria object
    ** This is (at least for some cases) for when the criteria gets changed
    ** from outside of this component - e.g., a reset on the query screen
    *******************************************************************************/
   const criteriaNeedsReset = (): boolean =>
   {
      if (criteria != null && criteriaParam == null)
      {
         const defaultOperatorOption = operatorOptions.filter(o => o.value == defaultOperator)[0];
         if (criteria.operator !== defaultOperatorOption?.value || JSON.stringify(criteria.values) !== JSON.stringify(getDefaultCriteriaValue()))
         {
            if (isOpen)
            {
               //////////////////////////////////////////////////////////////////////////////////
               // this was firing too-often for case where:  there was no criteria originally, //
               // so, by adding this is-open check, we eliminated those.                       //
               //////////////////////////////////////////////////////////////////////////////////
               console.log("Not handling outside change (B), because dropdown is-open");
               return (false);
            }

            return (true);
         }
      }

      return (false);
   };

   /*******************************************************************************
    ** Construct a new criteria object - resetting the values tied to the operator
    ** autocomplete at the same time.
    *******************************************************************************/
   const makeNewCriteria = (): QFilterCriteria =>
   {
      const operatorOption = operatorOptions.filter(o => o.value == defaultOperator)[0];
      const criteria = new QFilterCriteriaWithId(fullFieldName, operatorOption?.value, getDefaultCriteriaValue());
      criteria.id = id;
      setOperatorSelectedValue(operatorOption);
      setOperatorInputValue(operatorOption?.label);
      setCriteria(criteria);
      return (criteria);
   };

   /*******************************************************************************
    ** event handler to open the menu in response to the button being clicked.
    *******************************************************************************/
   const handleOpenMenu = (event: any) =>
   {
      setIsOpen(!isOpen);
      setAnchorEl(event.currentTarget);

      /////////////////////////////////////////////////////////////////////////////////////////////
      // we need the popover's open transition to finish before we touch the value input - else  //
      // an autocomplete popup anchored mid-animation lands in the wrong spot.  poll on every    //
      // animation frame and wait until the input's bounding rect has been identical on several  //
      // consecutive frames (i.e., layout is settled, not just briefly motionless in MUI's       //
      // Grow "appearing" phase before the transition has actually started animating).  cap the  //
      // total attempts so we don't loop forever if something goes sideways.  once stable: focus //
      // the value input, and if it's a PVS autocomplete (role="combobox") dispatch mousedown -  //
      // the event MUI Autocomplete listens for to open its popup - so the user can pick or type //
      // without an extra click.                                                                 //
      /////////////////////////////////////////////////////////////////////////////////////////////
      const valueColumnSelector = `#value-column-${criteria.id}`;
      let lastRect: {top: number; left: number; width: number; height: number} | null = null;
      let stableCount = 0;
      let attempts = 0;
      const requiredStableFrames = 3;
      const maxAttempts = 30;

      const findValueInput = (): HTMLElement | null =>
      {
         /////////////////////////////////////////////////////////////////////////////////////
         // prefer the well-known id used by plain-text TextField inputs; otherwise fall    //
         // back to the first input inside THIS quick filter's value column - scoping by id //
         // so we never pick up a different popover's input if one is mid-close.            //
         /////////////////////////////////////////////////////////////////////////////////////
         const byId = document.getElementById("value-" + criteria.id);
         if (byId)
         {
            return byId;
         }
         return document.querySelector(`${valueColumnSelector} input:not([type=hidden])`);
      };

      const applyFocus = (element: HTMLElement) =>
      {
         /////////////////////////////////////////////////////////////////////////////////////
         // bail out if focus has already moved to an input inside the popup before our     //
         // polling settled - e.g., the user (or a fast Selenium test) clicked the operator //
         // while we were still measuring.  on initial open, MUI Popover parks focus on the //
         // popover root (a div), so document.activeElement isn't an INPUT yet and we do    //
         // run our default focus-the-value-input behavior.                                 //
         /////////////////////////////////////////////////////////////////////////////////////
         const active = document.activeElement as HTMLElement | null;
         if (active && active !== element && active.tagName === "INPUT")
         {
            return;
         }

         element.focus();

         if (element instanceof HTMLInputElement && element.getAttribute("role") !== "combobox" && element.value)
         {
            ////////////////////////////////////////////////////////////////////////////////////
            // for a re-opened text/number/date filter that already has a value, select it so //
            // the user can immediately type a replacement without having to clear it first.  //
            ////////////////////////////////////////////////////////////////////////////////////
            element.select();
         }
      };

      const tryOpenInput = () =>
      {
         attempts++;
         const reachedCap = attempts >= maxAttempts;
         const element = findValueInput();

         /////////////////////////////////////////////////////////////////////////////////////
         // if we hit the cap, focus whatever input we have right now (or just return) - we //
         // never want to silently skip focus on a slow machine that hasn't settled yet.    //
         /////////////////////////////////////////////////////////////////////////////////////
         if (reachedCap)
         {
            if (element)
            {
               applyFocus(element);
            }
            return;
         }

         if (!element)
         {
            requestAnimationFrame(tryOpenInput);
            return;
         }

         const rect = element.getBoundingClientRect();
         const sameAsLast = lastRect
            && rect.top === lastRect.top
            && rect.left === lastRect.left
            && rect.width === lastRect.width
            && rect.height === lastRect.height;

         if (sameAsLast)
         {
            stableCount++;
         }
         else
         {
            stableCount = 0;
            lastRect = {top: rect.top, left: rect.left, width: rect.width, height: rect.height};
         }

         if (stableCount < requiredStableFrames)
         {
            requestAnimationFrame(tryOpenInput);
            return;
         }

         applyFocus(element);
      };

      requestAnimationFrame(tryOpenInput);
   };

   /*******************************************************************************
    ** handler for the Menu when being closed
    *******************************************************************************/
   const closeMenu = () =>
   {
      //////////////////////////////////////////////////////////////////////////////////
      // when closing the menu, that's when we'll update the criteria from the caller //
      //////////////////////////////////////////////////////////////////////////////////
      updateCriteria(criteria, false, false);

      setIsOpen(false);
      setAnchorEl(null);
   };

   /*******************************************************************************
    ** keydown handler for the value column - treats Enter the same as clicking
    ** the backdrop (submit + close + run the search).  skipped when focus is in a
    ** role="combobox" input so MUI Autocomplete's own Enter handling (selecting an
    ** option, adding a free-solo chip) is left alone - i.e., not for PVS or multi-
    ** value autocompletes; just for plain text fields, dates, date-times, and the
    ** From/To pairs of those.
    *******************************************************************************/
   const handleValueKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) =>
   {
      if (e.key !== "Enter")
      {
         return;
      }

      const target = e.target as HTMLElement;
      if (target.closest("[role=combobox]"))
      {
         return;
      }

      ///////////////////////////////////////////////////////////////////////////////////
      // React synthetic events bubble through the component tree, so a keypress in a  //
      // child portal (e.g., FilterCriteriaPaster's Modal textarea) still reaches this //
      // handler even though the target is DOM-detached from the value column.  only   //
      // treat Enter as "submit" when the keystroke originated from a control actually //
      // nested in this column's DOM subtree.                                          //
      ///////////////////////////////////////////////////////////////////////////////////
      if (!target.closest(".quickFilter.filterValuesColumn"))
      {
         return;
      }

      e.preventDefault();

      ////////////////////////////////////////////////////////////////////////////////////
      // capture the anchor before closing so we can target it specifically when the    //
      // popover's focus restore lands - if for some reason the restore is slow and the //
      // user has moved focus elsewhere, we don't want to blur an unrelated element.    //
      ////////////////////////////////////////////////////////////////////////////////////
      const anchorButton = anchorEl as HTMLElement | null;
      closeMenu();

      //////////////////////////////////////////////////////////////////////////////////
      // after popover unmount, MUI restores focus to its anchor (the quick filter    //
      // button).  because Enter sets the :focus-visible polyfill's keyboard-recently //
      // -used flag, the button then shows its focus-ripple pulse - which we don't    //
      // get from a mouse-driven backdrop close.  queue a blur after the restore so   //
      // focus lands on body, matching backdrop-click behavior.                       //
      //////////////////////////////////////////////////////////////////////////////////
      setTimeout(() =>
      {
         if (anchorButton && document.activeElement === anchorButton)
         {
            anchorButton.blur();
         }
      });
   };

   /*******************************************************************************
    ** event handler for operator Autocomplete having its value changed
    *******************************************************************************/
   const handleOperatorChange = (event: any, newValue: any, reason: string) =>
   {
      const newOperatorOption = newValue as OperatorOption;

      if (newOperatorOption)
      {
         criteria.operator = newOperatorOption.value;
         setOperatorSelectedValue(newOperatorOption);
         setOperatorInputValue(newOperatorOption.label);

         if (newOperatorOption.implicitValues)
         {
            criteria.values = newOperatorOption.implicitValues;
         }

         //////////////////////////////////////////////////////////////////////////////////////////////////
         // we've seen cases where switching operators can sometimes put a null in as the first value... //
         // that just causes a bad time (e.g., null pointers in Autocomplete), so, get rid of that.      //
         //////////////////////////////////////////////////////////////////////////////////////////////////
         if (criteria.values && criteria.values.length == 1 && criteria.values[0] == null)
         {
            criteria.values = [];
         }

         criteria.values = clampValuesToOperatorOption(criteria.values, newOperatorOption);

         /////////////////////////////////////////////////////////////////////////////////////////////////////
         // if the operator option has a field function, then set it on the criteria object - else clear it //
         /////////////////////////////////////////////////////////////////////////////////////////////////////
         if(newOperatorOption.fieldFunctionType)
         {
            criteria.fieldFunction = new FieldFunction(criteria.fieldName, newOperatorOption.fieldFunctionType, newOperatorOption.fieldFunctionArguments);
         }
         else
         {
            criteria.fieldFunction = undefined;
         }

         //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
         // for dates & date-times - if moving from a PVS operator to a date (or date time) operator, reset the values array //
         //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
         if((fieldMetaData?.type == "DATE" || fieldMetaData?.type == "DATE_TIME") && criteria.values.length > 0)
         {
            ////////////////////////////////////////////////////////////////////////////////////////////////////////////
            // at first we thought this code might fail if you move from pvs to none, then none to date...            //
            // but presumably that goes through a splice again, w/ required value count 0, thus making it a non-issue //
            ////////////////////////////////////////////////////////////////////////////////////////////////////////////
            const isPvs = (option: OperatorOption) => option?.valueMode == ValueMode.PVS_SINGLE || option?.valueMode == ValueMode.PVS_MULTI;
            const isDate = (option: OperatorOption) => option?.valueMode == ValueMode.SINGLE_DATE || option?.valueMode == ValueMode.DOUBLE_DATE || option?.valueMode == ValueMode.SINGLE_DATE_TIME || option?.valueMode == ValueMode.DOUBLE_DATE_TIME;

            if((isPvs(operatorSelectedValue) && isDate(newOperatorOption)) || (isDate(operatorSelectedValue) && isPvs(newOperatorOption)))
            {
               criteria.values = [];
            }
         }
      }
      else
      {
         criteria.operator = null;
         criteria.fieldFunction = undefined;
         setOperatorSelectedValue(null);
         setOperatorInputValue("");
      }

      setCriteria(criteria);
      forceUpdate();
   };

   /*******************************************************************************
    ** implementation of isOptionEqualToValue for Autocomplete - compares both the
    ** value (e.g., what operator it is) and the implicitValues within the option
    *******************************************************************************/
   function isOperatorOptionEqual(option: OperatorOption, value: OperatorOption)
   {
      return (option?.value == value?.value && JSON.stringify(option?.implicitValues) == JSON.stringify(value?.implicitValues));
   }

   /*******************************************************************************
    ** event handler for the value field (of all types), when it changes
    *******************************************************************************/
   const handleValueChange = (event: React.ChangeEvent | SyntheticEvent, valueIndex: number | "all" = 0, newValue?: any) =>
   {
      // @ts-ignore
      const value = newValue !== undefined ? newValue : event ? event.target.value : null;

      console.log("IN HERE");
      if (!criteria.values)
      {
         criteria.values = [];
      }

      if (valueIndex == "all")
      {
         criteria.values = value;
      }
      else
      {
         criteria.values[valueIndex] = value;
      }

      setCriteria(criteria);
      forceUpdate();
   };

   /*******************************************************************************
    ** a noop event handler, e.g., for a too-complex
    *******************************************************************************/
   const noop = () =>
   {
   };

   /*******************************************************************************
    ** event handler that responds to 'x' button that removes the criteria from the
    ** quick-filter, resetting it to a new filter.
    *******************************************************************************/
   const resetCriteria = (e: React.MouseEvent<HTMLSpanElement>) =>
   {
      if (criteriaIsValid)
      {
         e.stopPropagation();
         const newCriteria = makeNewCriteria();
         updateCriteria(newCriteria, false, true);
      }
   };

   /*******************************************************************************
    ** event handler for clicking the (x) icon that turns off this quick filter field.
    ** hands off control to the function that was passed in (e.g., from RecordQueryOrig).
    *******************************************************************************/
   const handleTurningOffQuickFilterField = () =>
   {
      closeMenu();
      if (handleRemoveQuickFilterField)
      {
         handleRemoveQuickFilterField(criteria?.fieldName);
      }
   };

   ////////////////////////////////////////////////////////////////////////////////////
   // if no field was input (e.g., record-query is still loading), return null early //
   ////////////////////////////////////////////////////////////////////////////////////
   if (!fieldMetaData)
   {
      return (null);
   }

   //////////////////////////////////////////////////////////////////////////////////////////
   // if there should be a selected value in the operator autocomplete, and it's different //
   // from the last selected one, then set the state vars that control that autocomplete   //
   //////////////////////////////////////////////////////////////////////////////////////////
   const maybeNewOperatorSelectedValue = getOperatorSelectedValue(operatorOptions, criteria, defaultOperator);
   if (JSON.stringify(maybeNewOperatorSelectedValue) !== JSON.stringify(operatorSelectedValue))
   {
      setOperatorSelectedValue(maybeNewOperatorSelectedValue);
      setOperatorInputValue(maybeNewOperatorSelectedValue?.label);
   }

   /////////////////////////////////////////////////////////////////////////////////////
   // if there wasn't a criteria, or we need to reset it (make a new one), then do so //
   /////////////////////////////////////////////////////////////////////////////////////
   if (criteria == null || criteriaNeedsReset())
   {
      makeNewCriteria();
   }

   /////////////////////////
   // build up the button //
   /////////////////////////
   const tooComplex = criteriaParam == "tooComplex";
   const tooltipEnterDelay = 500;

   let buttonAdditionalStyles: any = {};
   let buttonVariant: "outlined" | "contained" | "text" = "outlined";
   let buttonColorName: "secondary" | "primary" = "secondary";
   let buttonContent = <span>{tableForField?.name != tableMetaData.name ? `${tableForField.label}: ` : ""}{fieldMetaData.label}</span>;
   let buttonClassName = "filterNotActive";
   if (criteriaIsValid)
   {
      buttonVariant = "contained";
      buttonColorName = "primary";
      buttonAdditionalStyles.backgroundColor = accentColor + " !important";
      buttonAdditionalStyles.borderColor = accentColor + " !important";
      buttonAdditionalStyles.color = "white !important";
      buttonClassName = "filterActive";

      let valuesString = FilterUtils.getValuesString(fieldMetaData, criteria, 1, "+N");

      ///////////////////////////////////////////
      // don't show the Equals or In operators //
      ///////////////////////////////////////////
      ////////////////////////////////////////////////////////////////////////////////////
      // operatorSelectedValue can legitimately be null here - getOperatorSelectedValue //
      // returns null when neither the criteria's operator nor the default operator is  //
      // among this field's options, and that null gets committed to state above.  it   //
      // must not take the whole query screen down, so guard rather than dereference    //
      // (FilterCriteriaRow uses the same optional-chaining convention).                //
      ////////////////////////////////////////////////////////////////////////////////////
      let operatorString = operatorSelectedValue ? (<>{operatorSelectedValue.label}&nbsp;</>) : (<></>);
      if (operatorSelectedValue?.value == QCriteriaOperator.EQUALS || operatorSelectedValue?.value == QCriteriaOperator.IN)
      {
         operatorString = (<></>);
      }

      buttonContent = (<><span style={{fontWeight: 700}}>{buttonContent}:</span>&nbsp;<span style={{fontWeight: 400}}>{operatorString}{valuesString}</span></>);
   }

   const mouseEvents =
      {
         onMouseOver: () => handleMouseOverElement(),
         onMouseOut: () => handleMouseOutElement()
      };

   let button = fieldMetaData && <Button
      variant={buttonVariant}
      color={buttonColorName}
      id={`quickFilter.${fullFieldName}`}
      className={buttonClassName}
      {...mouseEvents}
      sx={{...quickFilterButtonStyles, ...buttonAdditionalStyles}}
      onClick={(tooComplex || isLocked) ? noop : handleOpenMenu}
      disabled={tooComplex}
   >{buttonContent}</Button>;

   /////////////////////////////////////////////////////////////////////////////////////
   // a locked quick filter edits a criterion that whatever opened this screen        //
   // requires (e.g. the possible-value source behind a dropdown).  it is shown, with //
   // its value, so the user understands the scope they are searching within - but it //
   // must not be editable, so the menu never opens (and the 'x' is not rendered).    //
   /////////////////////////////////////////////////////////////////////////////////////
   if (isLocked)
   {
      /////////////////////////////////////////////////////////////////////////////////////
      // the lock sits exactly where the 'x' would be on a removable quick filter - same //
      // trick XIcon uses: a zero-width relative span after the button, holding an       //
      // absolutely positioned icon pulled back over the button's right edge.            //
      /////////////////////////////////////////////////////////////////////////////////////
      return (
         <Tooltip title="This Filter cannot be edited" enterDelay={tooltipEnterDelay} slotProps={{popper: {sx: {top: "-0.25rem!important"}}}}>
            <span style={{whiteSpace: "nowrap"}}>
               {button}
               <span style={{position: "relative"}}>
                  <Icon sx={{
                     fontSize: "1rem !important",
                     color: accentColor,
                     background: "none",
                     border: "none",
                     borderRadius: 0,
                     padding: "0.125rem",
                     position: "absolute",
                     height: "1.5rem",
                     top: "-1rem",
                     left: "-1.375rem",
                  }}>lock</Icon>
               </span>
            </span>
         </Tooltip>
      );
   }

   ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
   // if the criteria on this field is the "tooComplex" sentinel, then wrap the button in a tooltip stating such, and return early. //
   // note this was part of original design on this widget, but later deprecated...                                                 //
   ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
   if (tooComplex)
   {
      ////////////////////////////////////////////////////////////////////////////
      // wrap button in span, so disabled button doesn't cause disabled tooltip //
      ////////////////////////////////////////////////////////////////////////////
      return (
         <Tooltip title={`Your current filter is too complex to do a Quick Filter on ${fieldMetaData.label}.  Use the Filter button to edit.`} enterDelay={tooltipEnterDelay} slotProps={{popper: {sx: {top: "-0.75rem!important"}}}}>
            <span>{button}</span>
         </Tooltip>
      );
   }

   /*******************************************************************************
    ** event handler for 'x' button - either resets the criteria or turns off the field.
    *******************************************************************************/
   const xClicked = (e: React.MouseEvent<HTMLSpanElement>) =>
   {
      e.stopPropagation();

      ///////////////////////////////////////////////////////////////////////////
      // belt-and-braces - a locked quick filter renders no X, but must not be //
      // clearable through this path either.                                   //
      ///////////////////////////////////////////////////////////////////////////
      if (isLocked)
      {
         return;
      }

      if (criteriaIsValid)
      {
         resetCriteria(e);
      }
      else
      {
         handleTurningOffQuickFilterField();
      }
   };

   //////////////////////////////
   // return the button & menu //
   //////////////////////////////
   const widthAndMaxWidth = (fieldMetaData?.type == QFieldType.DATE_TIME) ? 315 : fieldMetaData.possibleValueSourceName ? 300 : 250;
   /////////////////////////////////////////////////////////////////////////////////////////////
   // wrap the button + X in an inline whiteSpace:nowrap span so the X (a 0-width inline span //
   // placed after the button's marginRight) can't break to the next line on its own when     //
   // the button lands at the line edge - they stay together as a unit when the line wraps.   //
   /////////////////////////////////////////////////////////////////////////////////////////////
   return (
      <>
         <span style={{whiteSpace: "nowrap"}}>
            {button}
            {
               /////////////////////////////////////////////////////////////////////////////////////
               // only show the 'x' if it's to clear out a valid criteria on the field,           //
               // or if we were given a callback to remove the quick-filter field from the screen //
               /////////////////////////////////////////////////////////////////////////////////////
               !isLocked && (criteriaIsValid || handleRemoveQuickFilterField) && isMouseOver && <span {...mouseEvents}><XIcon shade={criteriaIsValid ? "accent" : "default"} position="forQuickFilter" onClick={xClicked} /></span>
            }
         </span>
         {
            isOpen && <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={closeMenu} sx={{overflow: "visible"}} anchorOrigin={{vertical: "bottom", horizontal: "left"}} transformOrigin={{vertical: "top", horizontal: "left"}} PaperProps={{sx: {background: "white"}}}>
               <Box display="inline-block" width={widthAndMaxWidth} maxWidth={widthAndMaxWidth} className="operatorColumn">
                  <Autocomplete
                     id={"criteriaOperator"}
                     ////////////////////////////////////////////////////////////////////////////////////////////////////
                     // ok, so, by default, if you type an 'o' as the first letter in the FilterCriteriaRowValues box, //
                     // something is causing THIS element to become selected, if the first letter in its label is 'O'. //
                     // ... work around is to put invisible &zwnj; entity as first character in label instead...       //
                     ////////////////////////////////////////////////////////////////////////////////////////////////////
                     renderInput={(params) => (<TextField {...params} label={<>&zwnj;Operator</>} variant="standard" autoComplete="off" type="search" InputProps={{...params.InputProps}} />)}
                     options={operatorOptions}
                     value={operatorSelectedValue as any}
                     inputValue={operatorInputValue}
                     onChange={handleOperatorChange}
                     onInputChange={(e, value) => setOperatorInputValue(value)}
                     isOptionEqualToValue={(option, value) => isOperatorOptionEqual(option, value)}
                     getOptionLabel={(option: any) => option.label}
                     autoSelect={true}
                     autoHighlight={true}
                     selectOnFocus={true}
                     disableClearable
                     slotProps={{popper: {className: "operatorColumnPopper", style: {padding: 0, maxHeight: "unset", width: "250px"}}}}
                  />
               </Box>
               <Box id={`value-column-${criteria.id}`} width={widthAndMaxWidth} maxWidth={widthAndMaxWidth} className="quickFilter filterValuesColumn" sx={{"& .MuiChip-root.MuiAutocomplete-tag": {maxWidth: `${widthAndMaxWidth - 85}px`}}} onKeyDown={handleValueKeyDown}>
                  <FilterCriteriaRowValues
                     queryScreenUsage={queryScreenUsage}
                     operatorOption={operatorSelectedValue}
                     criteria={criteria}
                     field={fieldMetaData}
                     table={tableForField}
                     allowVariables={allowVariables}
                     valueChangeHandler={(event, valueIndex, newValue) => handleValueChange(event, valueIndex, newValue)}
                  />
               </Box>
            </Popover>
         }
      </>
   );
}
